import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { RequestQueryPayload } from '@cureskin/api-client';
import { ConnectionService } from 'src/services/connection-service';
import { v4 as uuid } from 'uuid';
import { UploadFileService } from 'src/services/uploadFileService/uploadFile.service';
import { Broadcaster } from 'src/components/broadcaster/index';
import { AppConfig } from 'src/app/app.config';

@Component({
  selector: 'app-product-highlight-modal',
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
  ],
  templateUrl: './product-highlight-modal.component.html',
  styleUrls: ['./product-highlight-modal.scss'],
  standalone: true,
})
export class ProductHighlightModalComponent {
  data = inject(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<ProductHighlightModalComponent>);

  selectedHighlightImage: string | null = null;
  productHighlights: Array<Table.ProductHighlight> = [];

  imageFiles: Array<string> = [];
  uploadedImageUrl: string = '';
  name: string = uuid();
  accept: string = 'image/png, image/jpg, image/jpeg';
  constraints: { size?: number, aspectRadio?: number[] } = {
    size: 150000,
    aspectRadio: [1, 0.9, 1.1]
  };

  constructor(
    private readonly conn: ConnectionService,
    private readonly upload: UploadFileService,
    private readonly broadcasterService: Broadcaster,
    private readonly appConfig: AppConfig
  ) {}

  async ngOnInit(): Promise<void> {

    const payload: RequestQueryPayload<Table.ProductHighlight> = {
      where: {},
      include: ['image'],
    }
    const allHighlights = await this.conn.findProductHighlight(payload);
    const uniqueImageMap = new Map<string, Table.ProductHighlight>();
    allHighlights.forEach(highlight => {
      const imageUrl = highlight.get('image');
      if (imageUrl && !uniqueImageMap.has(imageUrl)) {
        uniqueImageMap.set(imageUrl, highlight);
      }
    });
    this.productHighlights = Array.from(uniqueImageMap.values());
  }

  hideModal(): void {
    this.dialogRef.close();
  }

  selectHighlight(highlight: Table.ProductHighlight): void {
    const imageUrl = highlight.get('image');

    if (this.selectedHighlightImage === imageUrl) {
      // Unselect if already selected
      this.selectedHighlightImage = null;
    } else if (this.imageFiles.length > 0) {
      // Don't allow selecting if there's an uploaded image
      this.broadcasterService.broadcast('NOTIFY', {
        message: 'Please remove the uploaded image before selecting an existing highlight.',
        type: this.appConfig.Shared.Toast.Type.ERROR
      });
    } else {
      this.selectedHighlightImage = imageUrl;
    }
  }

  async onFileChange(files: any): Promise<any> {
    if (this.imageFiles.length > 0) {
      alert('Only one image can be uploaded. Please remove the existing image first.');
      return;
    }

    if (this.selectedHighlightImage) {
      this.broadcasterService.broadcast('NOTIFY', {
        message: 'Please unselect the existing highlight before uploading a new image.',
        type: this.appConfig.Shared.Toast.Type.ERROR
      });
      return;
    }

    const file = files[0];
    if (!file) return;
    try {
      const src = await this.checkSize(file);
      await this.checkAspectRatio(src);
      await this.uploadImage(file);
    } catch (e: any) {
      alert(e);
    }
  }

  checkSize(file: File): Promise<string> {
    return new Promise((resolve: Function, reject: Function): void => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e: any): void => {
        const src = e.target.result;
        if (this.constraints?.size && file?.size > this.constraints?.size) {
          reject('Size should be less than 150KB');
        }
        resolve(src);
      };
    });
  }

  checkAspectRatio(imageSrc: any): Promise<boolean> {
    return new Promise((resolve: Function, reject: Function): void => {
      const image = new Image();
      image.onload = (): void => {
        if (this.constraints.aspectRadio && this.constraints.aspectRadio.length) {
          const actualRatio = image.width / image.height;
          const tolerance = 0.05;
          const isValidRatio = this.constraints.aspectRadio.some(
            (ratio: number) => Math.abs(actualRatio - ratio) < tolerance
          );
          if (!isValidRatio) {
            const actualRatioFormatted = actualRatio.toFixed(2);
            const expectedRatios = this.constraints.aspectRadio.map(r => r.toFixed(2)).join(', ');
            reject(`Image has aspect ratio of ${actualRatioFormatted}. Expected ratios: ${expectedRatios}. Current dimensions: ${image.width}x${image.height}`);
          }
        }
        resolve(true);
      };
      image.src = imageSrc;
    });
  }

  async uploadImage(imageFile: any): Promise<void> {
    const url = await this.upload.uploadFile({
      file: imageFile,
      bucket: 'PRODUCT',
      source: 'doctor_product',
      username: this.conn.getCurrentUser().get('username')
    });

    this.imageFiles.push(url);
    this.uploadedImageUrl = url;
  }

  removeFile(index: number): void {
    this.imageFiles.splice(index, 1);
    if (index === 0) {
      this.uploadedImageUrl = '';
    }
  }

  createAndsaveProductHighlight(): void {
    const selectedImage = this.selectedHighlightImage || this.uploadedImageUrl;
    if (selectedImage) {
      this.dialogRef.close(selectedImage);
    } else {
      this.broadcasterService.broadcast('NOTIFY', {
        message: 'Please select an image or upload a new one.',
        type: this.appConfig.Shared.Toast.Type.ERROR
      });
    }
  }
}
